<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ navigate: [] }>()

const auth = useAuthStore()
const route = useRoute()

interface NavItem {
  to: string
  label: string
  icon: string
  /** Zobrazí počet nepřečtených notifikací. */
  badge?: 'notifications'
}

const USER_ITEMS: NavItem[] = [
  { to: '/', label: 'Přehled', icon: 'M3 12h6V3H3v9zm0 9h6v-6H3v6zm12 0h6V12h-6v9zm0-18v6h6V3h-6z' },
  {
    to: '/offers',
    label: 'Nabídky',
    icon: 'M3.5 12.5l8-8H20v8.5l-8 8-8.5-8.5zM15.5 8.5h.01',
  },
  {
    to: '/cheapest',
    label: 'Nejlevnější',
    icon: 'M12 3v18m0 0l-6-6m6 6l6-6M5 3h14',
  },
  { to: '/compare', label: 'Srovnání', icon: 'M12 4v16M5 8h14M5 8l-2.5 6a3 3 0 005 0L5 8zm14 0l-2.5 6a3 3 0 005 0L19 8zM8 20h8' },
  {
    to: '/basket',
    label: 'Košík',
    icon: 'M3 4h2.5l2.2 10.5a1.5 1.5 0 001.5 1.2h8.6a1.5 1.5 0 001.5-1.1L21 8H6.2M10 20h.01M17 20h.01',
  },
  {
    to: '/planner',
    label: 'Plánovač nákupu',
    icon: 'M6 19a2 2 0 100-4 2 2 0 000 4zm12-10a2 2 0 100-4 2 2 0 000 4zM6 15V9a4 4 0 014-4h2m6 4v6a4 4 0 01-4 4h-2',
  },
  {
    to: '/map',
    label: 'Mapa prodejen',
    icon: 'M12 21s-7-6.2-7-11.5a7 7 0 0114 0C19 14.8 12 21 12 21zm0-9a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  },
  { to: '/leaflets', label: 'Letáky', icon: 'M7 3h7l5 5v13H7V3zm7 0v5h5M10 12h6M10 16h6' },
  {
    to: '/notifications',
    label: 'Notifikace',
    icon: 'M18 15V10a6 6 0 10-12 0v5l-2 3h16l-2-3zM10 21h4',
    badge: 'notifications',
  },
  { to: '/settings', label: 'Nastavení účtu', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6zm7.4-3a7.4 7.4 0 00-.1-1l2-1.6-2-3.4-2.4 1a7.5 7.5 0 00-1.7-1L14.8 3H9.2l-.4 2.6a7.5 7.5 0 00-1.7 1l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 000 2l-2 1.6 2 3.4 2.4-1a7.5 7.5 0 001.7 1l.4 2.6h5.6l.4-2.6a7.5 7.5 0 001.7-1l2.4 1 2-3.4-2-1.6c.06-.33.1-.66.1-1z' },
  {
    to: '/settings/mcp',
    label: 'Připojení AI',
    icon: 'M9 3v4m6-4v4M7 7h10v4a5 5 0 01-10 0V7zm5 9v5',
  },
  {
    to: '/components',
    label: 'Komponenty',
    icon: 'M4 5.5A1.5 1.5 0 015.5 4h4A1.5 1.5 0 0111 5.5v4A1.5 1.5 0 019.5 11h-4A1.5 1.5 0 014 9.5v-4zm9 9a1.5 1.5 0 011.5-1.5h4a1.5 1.5 0 011.5 1.5v4a1.5 1.5 0 01-1.5 1.5h-4a1.5 1.5 0 01-1.5-1.5v-4zM16.5 4L20 9h-7l3.5-5zM7.5 13a3.5 3.5 0 110 7 3.5 3.5 0 010-7z',
  },
]

const ADMIN_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Přehled aplikace', icon: 'M3 12h6V3H3v9zm0 9h6v-6H3v6zm12 0h6V12h-6v9zm0-18v6h6V3h-6z' },
  { to: '/admin/users', label: 'Uživatelé', icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zm-8 8a8 8 0 0116 0' },
  { to: '/admin/visits', label: 'Návštěvnost', icon: 'M4 19V9m5 10V5m5 14v-7m5 7V8' },
]

const isAdminSection = computed(() => route.meta.section === 'admin')
const items = computed(() => (isAdminSection.value ? ADMIN_ITEMS : USER_ITEMS))

/**
 * Aktivní položka i na podstránkách (detail letáku zvýrazní „Letáky“).
 * Vyhrává nejdelší shoda, takže /settings/mcp nezvýrazní i /settings.
 * Kořen „/“ se zvýrazní jen přesnou shodou.
 */
const activeTo = computed(() => {
  const path = typeof route.path === 'string' ? route.path : ''
  return items.value
    .filter((item) => path === item.to || (item.to !== '/' && path.startsWith(`${item.to}/`)))
    .reduce<string | null>((best, item) => (best === null || item.to.length > best.length ? item.to : best), null)
})
</script>

<template>
  <aside
    class="sidebar"
    :class="{ 'is-open': open, 'is-admin': isAdminSection }"
  >
    <div class="sidebar__brand">
      <span
        class="sidebar__logo"
        aria-hidden="true"
      >L</span>
      <div class="sidebar__names">
        <span class="sidebar__name">Lets</span>
        <span class="sidebar__section">{{ isAdminSection ? 'Administrace' : 'Letáky a ceny' }}</span>
      </div>
    </div>

    <nav
      class="sidebar__nav"
      :aria-label="isAdminSection ? 'Administrace' : 'Uživatelská sekce'"
    >
      <RouterLink
        v-for="item in items"
        :key="item.to"
        :to="item.to"
        class="sidebar__link"
        :class="{ 'is-active': item.to === activeTo }"
        @click="emit('navigate')"
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            :d="item.icon"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>{{ item.label }}</span>
        <span
          v-if="item.badge === 'notifications' && auth.user?.unreadNotifications"
          class="sidebar__count"
        >
          {{ auth.user.unreadNotifications }}
        </span>
      </RouterLink>
    </nav>

    <div class="sidebar__footer">
      <!-- Přepínač sekcí se ukáže jen tomu, kdo má přístup do obou. -->
      <RouterLink
        v-if="auth.isAdmin"
        :to="isAdminSection ? '/' : '/admin'"
        class="sidebar__switch"
        @click="emit('navigate')"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 8h13l-3-3m6 11H7l3 3"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ isAdminSection ? 'Zpět do mého účtu' : 'Přejít do administrace' }}
      </RouterLink>

      <a
        href="/api/docs"
        target="_blank"
        rel="noopener"
        class="sidebar__docs"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 4h9l4 4v12H6V4z M15 4v4h4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />
        </svg>
        Dokumentace API
      </a>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  width: var(--sidebar-width);
  height: 100vh;
  position: fixed;
  inset: 0 auto 0 0;
  background: var(--surface-card);
  border-right: 1px solid var(--border-subtle);
  z-index: 40;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: var(--topbar-height);
  padding: 0 var(--space-5);
  border-bottom: 1px solid var(--border-subtle);
}

.sidebar__logo {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: var(--accent-text);
  font-size: 12px;
  font-weight: 700;
}

/* Administrace se barevně odliší, ať je na první pohled jasné, kde uživatel je. */
.sidebar.is-admin .sidebar__logo { background: var(--warning); color: #fff; }

.sidebar__names { display: flex; flex-direction: column; line-height: 1.25; }
.sidebar__name { font-weight: 620; color: var(--text-strong); font-size: 13.5px; }
.sidebar__section { font-size: 11.5px; color: var(--text-muted); }
.sidebar.is-admin .sidebar__section { color: var(--warning); font-weight: 600; }

.sidebar__nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-4) var(--space-3);
  overflow-y: auto;
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 8px var(--space-3);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;
  transition: background var(--transition), color var(--transition);
}

.sidebar__link:hover {
  background: var(--surface-hover);
  color: var(--text-strong);
  text-decoration: none;
}

.sidebar__link.is-active {
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
}

.sidebar.is-admin .sidebar__link.is-active {
  background: var(--warning-soft);
  color: var(--warning);
}

.sidebar__count {
  margin-left: auto;
  min-width: 19px;
  padding: 1px 6px;
  border-radius: var(--radius-full);
  background: var(--accent);
  color: var(--accent-text);
  font-size: 11px;
  font-weight: 650;
  text-align: center;
}

.sidebar__footer {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.sidebar__switch,
.sidebar__docs {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 7px var(--space-3);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 13px;
}

.sidebar__switch {
  color: var(--accent);
  font-weight: 550;
  background: var(--accent-soft);
}

.sidebar.is-admin .sidebar__switch { color: var(--warning); background: var(--warning-soft); }

.sidebar__switch:hover,
.sidebar__docs:hover { text-decoration: none; filter: brightness(0.97); }
.sidebar__docs:hover { background: var(--surface-hover); color: var(--text-strong); }

@media (max-width: 900px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform var(--transition);
    box-shadow: var(--shadow-lg);
  }
  .sidebar.is-open { transform: translateX(0); }
}
</style>
